// src/components/orders/OrderFilters.tsx
import React from 'react';
import { Row, Col, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SearchBox from 'components/common/SearchBox';
import SortPill from 'components/common/SortPill';
import FilterButtonGroup from 'components/common/FilterButtonGroup';
import { FilterMenu } from 'components/common/FilterButtonGroup';

const sortControlsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  marginBottom: '16px'
};

interface OrderFiltersProps {
  // Search state
  customerNameSearch: string;
  orderNumberSearch: string;
  onCustomerNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOrderNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCustomerNameKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onOrderNumberKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  
  // Date filters
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  
  // Sort toggles
  sortOrderNumber: number | null;
  sortOrderDate: number | null;
  sortTotalAmount: number | null;
  toggleSortOrderNumber: () => void;
  toggleSortOrderDate: () => void;
  toggleSortTotalAmount: () => void;
  
  // Filter menus
  filterMenus: FilterMenu[];
  
  // Optional components to display inline with filters
  customizeButton?: React.ReactNode;
  actionButtons?: React.ReactNode;
}

const OrderFilters: React.FC<OrderFiltersProps> = ({
  customerNameSearch,
  orderNumberSearch,
  onCustomerNameChange,
  onOrderNumberChange,
  onCustomerNameKeyPress,
  onOrderNumberKeyPress,
  
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  
  sortOrderNumber,
  sortOrderDate,
  sortTotalAmount,
  toggleSortOrderNumber,
  toggleSortOrderDate,
  toggleSortTotalAmount,
  
  filterMenus,
  customizeButton,
  actionButtons
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Search and Filter Controls */}
      <Row className="g-3 mb-3">
        <Col xs={12} md={6} xl={3}>
          <Form.Group>
            <Form.Label>{t('orderFilters.orderNumber')}</Form.Label>
            <InputGroup>
              <SearchBox
                placeholder={t('orderFilters.searchByOrderNumber')}
                value={orderNumberSearch}
                onChange={onOrderNumberChange}
                onKeyPress={onOrderNumberKeyPress}
              />
            </InputGroup>
          </Form.Group>
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Form.Group>
            <Form.Label>{t('orderFilters.customerName')}</Form.Label>
            <InputGroup>
              <SearchBox
                placeholder={t('orderFilters.searchByCustomerName')}
                value={customerNameSearch}
                onChange={onCustomerNameChange}
                onKeyPress={onCustomerNameKeyPress}
              />
            </InputGroup>
          </Form.Group>
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Form.Group>
            <Form.Label>{t('orderFilters.fromDate')}</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => onFromDateChange(e.target.value)}
              placeholder={t('orderFilters.datePlaceholder')}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Form.Group>
            <Form.Label>{t('orderFilters.toDate')}</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => onToDateChange(e.target.value)}
              placeholder={t('orderFilters.datePlaceholder')}
              min={fromDate}
            />
          </Form.Group>
        </Col>
      </Row>
      
      {/* Custom Styled Sort Controls */}
      <div style={sortControlsContainerStyle}>
        <SortPill
          label={t('orderFilters.sort.orderNumber')}
          isActive={sortOrderNumber !== null ? sortOrderNumber : false}
          onClick={toggleSortOrderNumber}
          ariaLabel={t('orderFilters.aria.sortByOrderNumber')}
        />
        
        <SortPill
          label={t('orderFilters.sort.orderDate')}
          isActive={sortOrderDate !== null ? sortOrderDate : false}
          onClick={toggleSortOrderDate}
          ariaLabel={t('orderFilters.aria.sortByOrderDate')}
        />
        
        <SortPill
          label={t('orderFilters.sort.totalAmount')}
          isActive={sortTotalAmount !== null ? sortTotalAmount : false}
          onClick={toggleSortTotalAmount}
          ariaLabel={t('orderFilters.aria.sortByTotalAmount')}
        />
      </div>
      
      {/* Filter button group and custom button */}
      <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <div className="scrollbar overflow-hidden-y">
            <FilterButtonGroup menus={filterMenus} />
          </div>
          
          {/* Display customize button if provided */}
          {customizeButton && (
            <div className="d-flex align-items-center">
              {customizeButton}
            </div>
          )}
        </div>
        
        {/* Action buttons section */}
        {actionButtons && (
          <div className="ms-auto d-flex align-items-center">
            {actionButtons}
          </div>
        )}
      </div>
    </>
  );
};

export default OrderFilters;
